import { CategoryObject } from "../context/AuthContext/AuthContextProvider";

export interface CategoryNode {
  id: string;
  databaseId: number;
  slug: string;
  name: string;
  children?: {
    nodes: CategoryNode[];
  };
}

export interface FlatCategory {
  id: string;
  databaseId: number;
  slug: string;
  name: string;
  parent?: {
    node: {
      id: string;
      databaseId: number;
      slug: string;
      name: string;
    };
  };
  children: {
    nodes: FlatCategory[];
  };
}


export const buildCategoryTree = (flatCategories: FlatCategory[]): CategoryNode[] => {
  if (!flatCategories || flatCategories.length === 0) {
    return [];
  }

  const categoryMap = new Map<number, CategoryNode>();
  
  flatCategories.forEach(category => {
    if (!category || category.databaseId == null) {
      return;
    }

    categoryMap.set(category.databaseId, {
      id: category.id,
      databaseId: category.databaseId,
      slug: category.slug,
      name: category.name,
      children: { nodes: [] }
    });
  });

  const rootCategories: CategoryNode[] = [];
  
  flatCategories.forEach(category => {
    if (!category || category.databaseId == null) {
      return;
    }

    const categoryNode = categoryMap.get(category.databaseId);
    if (!categoryNode) return;

    if (category.parent?.node?.databaseId) {
      // This is a child category
      const parentNode = categoryMap.get(category.parent.node.databaseId);
      if (parentNode) {
        parentNode.children!.nodes.push(categoryNode);
      }
    } else {
      // This is a root category
      rootCategories.push(categoryNode);
    }
  });

  return rootCategories;
};


export const getCategoryDescendants = (
  categoryTree: CategoryNode[],
  parentCategoryIds: string[]
): string[] => {

  if (!categoryTree || !parentCategoryIds || parentCategoryIds.length === 0) {
    return [];
  }

  const descendantIds: string[] = [];

  const collectDescendants = (category: CategoryNode): string[] => {
    if (!category || category.databaseId == null) {
      return [];
    }

    const ids = [category.databaseId.toString()];
    
    if (category.children?.nodes) {
      category.children.nodes.forEach(child => {
        if (child) {
          ids.push(...collectDescendants(child));
        }
      });
    }
    
    return ids;
  };

  const findCategoriesById = (categories: CategoryNode[], targetIds: string[]): CategoryNode[] => {
    const found: CategoryNode[] = [];
    
    categories.forEach(category => {
      if (!category || category.databaseId == null) {
        return;
      }

      if (targetIds.includes(category.databaseId.toString())) {
        found.push(category);
      }
      
      if (category.children?.nodes) {
        found.push(...findCategoriesById(category.children.nodes, targetIds));
      }
    });
    
    return found;
  };

  const parentCategories = findCategoriesById(categoryTree, parentCategoryIds);

  parentCategories.forEach(parent => {
    descendantIds.push(...collectDescendants(parent));
  });

  return Array.from(new Set(descendantIds));
};


export const getCategoryDescendantsFromShowCategories = (
  categoryTree: CategoryNode[],
  showCategories: CategoryObject[]
): string[] => {
  if (!showCategories || showCategories.length === 0) {
    return [];
  }

  const parentCategoryIds = showCategories
    .filter((cat) => cat && cat.databaseId != null)
    .map((cat) => cat.databaseId.toString());
  
  return getCategoryDescendants(categoryTree, parentCategoryIds);
};


export const hasPriceCategoryAccess = (
  showPriceCategories?: CategoryObject[] | false | null
): boolean => {
  if (showPriceCategories === false || showPriceCategories == null) {
    return false;
  }

  return (
    Array.isArray(showPriceCategories) &&
    showPriceCategories.some((category) => category && category.databaseId != null)
  );
};

export const shouldShowPriceForCategories = (
  productCategoryIds: number[],
  showPriceCategories: CategoryObject[],
  allCategories: FlatCategory[]
): boolean => {
  if (!hasPriceCategoryAccess(showPriceCategories)) {
    return false;
  }

  if (!productCategoryIds || productCategoryIds.length === 0) {
    return false;
  }

  if (!allCategories || allCategories.length === 0) {
    return false; 
  }

  const categoryMapByDatabaseId = new Map<number, FlatCategory>();
  allCategories.forEach((category) => {
    if (!category || category.databaseId == null) {
      return;
    }
    categoryMapByDatabaseId.set(category.databaseId, category);
  });

  const allowedCategoryIds = new Set<number>();
  
  showPriceCategories.forEach((parentCategory) => {
    if (!parentCategory || parentCategory.databaseId == null) {
      return;
    }

    const parentId = parentCategory.databaseId;
    
    allowedCategoryIds.add(parentId);
    
    const getChildrenRecursively = (parentDatabaseId: number) => {
      allCategories.forEach((category) => {
        if (
          category?.databaseId != null &&
          category.parent?.node?.databaseId === parentDatabaseId
        ) {
          allowedCategoryIds.add(category.databaseId);
          getChildrenRecursively(category.databaseId);
        }
      });
    };
    
    getChildrenRecursively(parentId);
  });

  return productCategoryIds.some(productCategoryId => {
    return allowedCategoryIds.has(productCategoryId);
  });
}; 